import { CrudTableService } from '@/components/business/crud/service/CrudTableService';
import { windows } from '@/components/common/window/Windows';
import ImportProxy from '@/views/management/proxy/import/ImportProxy.vue';
import { markRaw, shallowRef } from 'vue';
import BindDevice from '@/views/management/proxy/bind/BindDevice.vue';
import { message, Modal } from 'ant-design-vue';
import DeviceSelect from '@/views/management/proxy/bind/DeviceSelect.vue';
import { request } from '@/util/request';
import InfoTableField from '@/views/management/proxy/InfoTableField.vue';
import ResultTableField from '@/views/management/proxy/ResultTableField.vue';
import DeviceTableField from '@/views/management/proxy/DeviceTableField.vue';
import ReplaceProxy from '@/views/management/proxy/replace/ReplaceProxy.vue';
import ExportForm from '@/views/management/proxy/export/ExportForm.vue';
import dayjs from 'dayjs';

export default class ProxyTable extends CrudTableService {
  private showDevice = false;

  async init() {
    this.widgetContainer.hiddenFields(
      'type',
      'host',
      'port',
      'username',
      'outIp',
      'area'
    );
    this.widgetContainer.addField(
      'info',
      '代理信息',
      {
        name: 'custom',
        component: markRaw(InfoTableField),
        meta: { size: { edit: 'large', show: 'large' } },
      },
      { order: 1.1 }
    );
    this.widgetContainer.addField(
      'result',
      '出口IP/归属地',
      {
        name: 'custom',
        component: markRaw(ResultTableField),
        meta: { size: { edit: 'large', show: 'large' } },
      },
      { order: 1.2 }
    );
    if (this.widgetContainer.fields.value.devices) {
      this.showDevice = true;
      this.widgetContainer.updateField(
        'widget',
        {
          name: 'custom',
          component: markRaw(DeviceTableField),
          extra: {
            refresh: this.refreshList.bind(this),
          },
        },
        'devices'
      );
    }
    this.addMyButtons();
    await super.init();
  }

  protected getLoadProps(loadProps: any[] = []): any[] {
    const value = super.getLoadProps(loadProps);
    value.push('type', 'host', 'port', 'username', 'outIp', 'area', 'password');
    if (this.showDevice) {
      value.push({
        propName: 'devices',
        param: {
          orderBy: [{ order: 'desc', column: 'createTime' }],
          limit: 3,
          aggregate: {
            count: {
              op: 'count',
              propName: '*',
            },
          },
        },
      });
    }
    return value.filter((i) => i && i !== 'info' && i != 'result');
  }

  private addMyButtons() {
    this.widgetContainer.addButton('import', {
      type: 'table',
      icon: 'ImportOutlined',
      text: '导入',
      handler: async () => {
        const id = windows.open({
          content: {
            type: 'component',
            component: shallowRef(ImportProxy),
            props: {
              onSubmit: (data: any[]) =>
                request('management/proxy/import', { data }),
              selectTagGroup: true,
            },
          },
          title: '导入代理线路',
        });
        const res = await windows.getCloseData(id);
        if (res) {
          await this.refreshList();
        }
      },
    });
    this.widgetContainer.addButton('export', {
      type: 'select',
      icon: 'ExportOutlined',
      text: '导出',
      handler: async (rows: any[]) => {
        if (rows.length === 0) {
          message.info('未选中线路');
          return;
        }
        const id = windows.open({
          content: {
            type: 'component',
            component: shallowRef(ExportForm),
          },
          mask: true,
          width: 800,
          height: 300,
          title: '导出代理线路',
        });
        const fmt = await windows.getCloseData(id);
        if (fmt !== false) {
          /**
           *  'ip:端口 账号 密码',
           *   'ip|端口|账号|密码|到期日期',
           *   'ip/端口/账号/密码',
           *   'ip/端口/账号/密码/地区',
           */
          const exportMethod = [
            () =>
              rows
                .map((i) => `${i.host}:${i.port} ${i.username} ${i.password}`)
                .join('\n'),
            () =>
              rows
                .map((i) => {
                  let text = `${i.host}|${i.port}|${i.username}|${i.password}|`;
                  if (i.expiredTime) {
                    text += dayjs(Number(i.expiredTime)).format('YYYY-MM-DD HH:mm:ss');
                  }
                  return text;
                })
                .join('\n'),
            () =>
              rows
                .map((i) => `${i.host}/${i.port}/${i.username}/${i.password}`)
                .join('\n'),
            () =>
              rows
                .map((i) => {
                  let text = `${i.host}/${i.port}/${i.username}/${i.password}/`
                  if (i.area) {
                    text += i.area.replaceAll(/\/|\s+/g,"-");
                  }
                  return text;
                })
                .join('\n'),
          ];
          this.saveFile(
            exportMethod[fmt](),
            '导出线路-' + dayjs().format('YYYYMMDD-HHmm') + '.txt'
          );
        }
      },
    });
    this.widgetContainer.addButton('check', {
      type: 'select',
      icon: 'MonitorOutlined',
      text: '线路检测',
      handler: async (rows: any) => {
        if (rows.length === 0) {
          message.info('未选中线路');
          return;
        }
        const res = await request('management/proxy/check', {
          ids: rows.map((i: any) => i.id),
        });
        if (res) {
          message.info('检测完成');
          await this.refreshList();
        }
      },
    });
    this.widgetContainer.addButton('replace', {
      type: 'select',
      icon: 'Loading3QuartersOutlined',
      text: '替换线路',
      handler: async (rows: any) => {
        if (rows.length === 0) {
          message.info('未选中线路');
          return;
        }
        const id = windows.open({
          width: 1400,
          content: {
            type: 'component',
            component: shallowRef(ReplaceProxy),
            props: {
              proxies: rows,
            },
          },
          title: '选择要绑定的设备',
        });
        const res = await windows.getCloseData(id);
        if (res) {
          await this.refreshList();
        }
      },
    });
    this.widgetContainer.addButton('bind', {
      type: 'select',
      icon: 'ShrinkOutlined',
      text: '绑定设备',
      handler: async (rows: any) => {
        if (rows.length === 0) {
          message.info('未选中线路');
          return;
        }
        const selectWindowId = windows.open({
          content: {
            type: 'component',
            component: shallowRef(DeviceSelect),
          },
          title: '选择要绑定的设备',
        });
        const selectRes = await windows.getCloseData(selectWindowId);
        if (!selectRes) {
          return;
        }
        const { selected, num } = selectRes;
        if (selected.length === 0) {
          message.error('未选中设备');
          return;
        }
        const id = windows.open({
          content: {
            type: 'component',
            component: shallowRef(BindDevice),
            props: {
              proxies: rows,
              devices: selected,
              num,
            },
          },
          title: '绑定设备',
        });
        const res = await windows.getCloseData(id);
        if (res) {
          await this.refreshList();
        }
      },
    });
    this.widgetContainer.addButton('unbind', {
      type: 'select',
      icon: 'ArrowsAltOutlined',
      text: '解绑设备',
      handler: async (rows: any) => {
        if (rows.length === 0) {
          message.info('未选中线路');
          return;
        }
        Modal.confirm({
          title: '确定要解绑这' + rows.length + '条线路的设备吗？',
          onOk: async () => {
            const res = await request('management/proxy/unbind', {
              ids: rows.map((i: any) => i.id),
            });
            if (res) {
              await this.refreshList();
            }
          },
        });
      },
    });
  }

  private saveFile(data: string, name: string) {
    // 创建 Blob 对象，指定 MIME 类型为 text/plain
    const blob = new Blob([data], { type: 'text/plain' });

    // 创建一个临时的 URL
    const url = window.URL.createObjectURL(blob);

    // 创建一个隐藏的 <a> 元素
    const link = document.createElement('a');
    link.href = url;
    link.download = name; // 设置下载的文件名

    // 将 <a> 元素添加到 DOM 中（可选，某些浏览器需要）
    document.body.appendChild(link);

    // 模拟点击链接以触发下载
    link.click();

    // 移除 <a> 元素
    document.body.removeChild(link);

    // 释放创建的 URL 对象
    window.URL.revokeObjectURL(url);
  }
}
